import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { proposalRouteConfig } from 'src/app/graph/routes';
import { Form } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

const validationSchema = Yup.object().shape({
  displayName: Yup.string().required(
    'Please provide a title for your proposal.',
  ),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const RenameProposalModal = ({
  proposalId,
  isOpen,
  onClose,
  currentDisplayName,
}: {
  proposalId: string;
  isOpen: boolean;
  onClose: () => void;
  currentDisplayName: string;
}) => {
  const { graphId, graphVariant } = useRouteParams(proposalRouteConfig);
  const [errorMessage, setErrorMessage] = useState('');
  const [renameProposal] = useMutation<
    GraphQLTypes.RenameProposalTitleMutation,
    GraphQLTypes.RenameProposalTitleMutationVariables
  >(gql`
    mutation RenameProposalTitleMutation(
      $graphId: ID!
      $graphVariant: String!
      $displayName: String!
    ) {
      graph(id: $graphId) {
        variant(name: $graphVariant) {
          proposal {
            ... on ProposalMutation {
              updateDisplayName(displayName: $displayName) {
                ... on ValidationError {
                  message
                }
                ... on PermissionError {
                  message
                }
                ... on Proposal {
                  id
                  displayName
                }
              }
            }
          }
        }
      }
    }
  `);

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    isValid,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      displayName: currentDisplayName,
    },
    validationSchema,
    onSubmit: async ({ displayName }) => {
      const { data: responseData } = await renameProposal({
        variables: {
          graphId,
          graphVariant,
          displayName,
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              __typename: 'Proposal',
              id: proposalId,
            }),
            fieldName: 'displayName',
          });
        },
      });

      const responseProposal = responseData?.graph?.variant?.proposal;
      if (
        responseProposal?.__typename === 'ProposalMutation' &&
        responseProposal?.updateDisplayName.__typename === 'Proposal'
      ) {
        onClose();
        resetForm({});
        setErrorMessage('');
      } else if (
        responseProposal?.__typename === 'ValidationError' ||
        responseProposal?.__typename === 'NotFoundError' ||
        responseProposal?.__typename === 'PermissionError' ||
        responseProposal?.updateDisplayName.__typename === 'PermissionError' ||
        responseProposal?.updateDisplayName.__typename === 'ValidationError' ||
        !responseProposal?.updateDisplayName
      ) {
        setErrorMessage('An error has occurred');
      } else if (responseProposal.updateDisplayName.__typename !== 'Proposal') {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            responseProposal.updateDisplayName,
            'An error has occurred',
          ),
        );
      }
    },
  });

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onCloseModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Rename your proposal</ModalTitle>
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <FormControl
              isRequired
              isInvalid={!!errors.displayName && !!touched.displayName}
            >
              <FormLabel>Proposal title</FormLabel>
              <Input
                name="displayName"
                placeholder={'e.g. "Add fields to type User"'}
                value={values.displayName}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.displayName}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" onClick={onCloseModal} type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                loadingText="Saving"
                isDisabled={
                  !isValid || currentDisplayName === values.displayName
                }
                type="submit"
              >
                Save
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
